/* eslint-disable semi */
import { createDecorator } from 'vue-class-component';
import * as utils from './utils';
import DecoratorConfigs from './DecoratorConfigs';

const debug = process.env.NODE_ENV === 'development';

const DECORATOR_NAME = 'BindForm';

/**
 * そのコンポーネントがフォームと連携することを宣言します
 *
 * 下記のパラメータを指定できます。
 * ref: フォームのref名を指定します。
 * resetBefore: 実行前にフォームの初期化を行う処理を指定します。
 * bindedErrors: フォームに関連付けされているエラー変数を指定します。
 * notify: バリデーションエラー時の通知有無を指定します。
 * notificator: 通知方法を指定します。
 *
 * resetBeforeで指定された処理時に、フォームのclearValidateメソッド
 * を実行します。
 *
 * フォームのvalidateメソッド実行時と、resetBeforeで指定された処理時
 * に関連付けされているエラー変数にデフォルト値(dataメソッドで取得した値)
 * を設定します。
 *
 * notifyがtrueの場合、フォームのvalidateで例外がスローされた場合に
 * notificatorを実行します。notificatorには任意の関数を指定できます。
 *
 * notificator関数内におけるthisはコンポーネント自身となります。
 *
 * 基本的に、連携するフォームはel-formコンポーネントである前提ですが、
 * validateとclearValidateメソッドが実装されているコンポーネントであれば
 * 他のコンポーネントでも動作します。
 */
export default function BindForm (...args) {
  if (utils.isDecoratorCall(args)) {
    createAppDecorator()(...args);
  } else {
    return createAppDecorator(...args);
  }
}

function createAppDecorator (decoratorOptions = {}) {
  // 設定値のマージ
  decoratorOptions = DecoratorConfigs.marge(DECORATOR_NAME, decoratorOptions);

  // 実装時の整合性チェック
  if (debug) {
    utils.validateDecoratorOptions(
      DECORATOR_NAME, {
        ref: { type: 'string', mustBe: ['not empty'] },
        resetBefore: { type: 'array', shouldBe: ['not empty'] },
        bindedErrors: { type: 'array', shouldBe: ['not empty'] },
        notify: { type: 'boolean' },
        notificator: { type: 'function' }
      },
      decoratorOptions
    );
  }

  return createDecorator((options, key) => {
    // 実装時の整合性チェック
    if (debug) {
      console.debug(`${DECORATOR_NAME}: @ ${key}`);

      if (key !== 'data') {
        throw new Error(`${DECORATOR_NAME}: can decorate only data().`);
      }
    }

    // オプション値取得
    const ref = decoratorOptions.ref || 'form';
    const resetBefore = decoratorOptions.resetBefore || [];
    const bindedErrors = decoratorOptions.bindedErrors || [];
    const notify = decoratorOptions.notify || false;
    const notificator =
      decoratorOptions.notificator ||
      function () {
        alert('Validation failed.');
      };

    // データメソッド
    const data = options.data;

    // 関連付けされているエラーのリセット処理
    const resetErrors = function (vue) {
      bindedErrors.forEach(label => {
        if (debug) {
          console.debug(`${DECORATOR_NAME}: reset ${label}`);
        }
        vue[label] = data()[label];
      });
    };

    // created時の処理を追加
    const created = options.created || function () { };
    options.created = utils.injectBefore(created, function () {
      // 実装時の整合性チェック
      if (debug) {
        if (bindedErrors) {
          bindedErrors
            .filter(label => !(label in this))
            .forEach(label => {
              console.error(
                `${DECORATOR_NAME}: ${label} must be declared in data().`
              );
            });
        }
      }

      this.$_sas = this.$_sas || {};
      this.$_sas.$_form = this.$_sas.$_form || {};

      // $refs[ref]の記述子を定義
      // setterのタイミングでコンポーネントを加工します
      Object.defineProperty(this.$refs, ref, {
        configurable: true,
        enumerable: true,
        get: () => {
          return this.$_sas.$_form[ref];
        },
        set: form => {
          // NOTE
          // コンポーネントのdestroyedのタイミングではundefinedがsetされるので注意。
          delete this.$_sas.$_form[ref];
          if (form && typeof form.validate === 'function') {
            const validate = form.validate;
            form.validate = (...args) => {
              // validateのタイミングで
              // 関連付けされているコンポーネント側のエラーをリセットする
              resetErrors(this);

              // バリデーション実行前にnextTick()を実行する
              // バリデーションエラー時に通知処理を行う
              return this.$nextTick().then(() => {
                return validate(args).catch(reason => {
                  notify && notificator.apply(this);
                  throw reason;
                });
              });
            };
            this.$_sas.$_form[ref] = form;
          }
        }
      });
    });

    // 関数をラップして事前処理(状態クリア)を追加する
    utils.wrapMethods(options.methods, resetBefore, function () {
      // 関連付けされているコンポーネント側のエラーデータをリセットする
      resetErrors(this);

      const form = this.$refs[ref];
      if (form) {
        form.clearValidate();
      }
    });

    // destroyed時の処理を追加
    const destroyed = options.destroyed || function () { };
    options.destroyed = utils.injectAfter(destroyed, function () {
      // デコレータ管理用変数破棄
      delete this.$refs[ref];
      if (this.$_sas) {
        if (this.$_sas.$_form) {
          delete this.$_sas.$_form[ref];
          if (Object.keys(this.$_sas.$_form).length === 0) {
            delete this.$_sas.$_form;
          }
        }
        if (Object.keys(this.$_sas).length === 0) {
          delete this.$_sas;
        }
      }
    });
  });
}



// WEBPACK FOOTER //
// ./src/core/decorators/BindForm.js